import React, { useEffect, useRef, useState } from 'react';
import AuthShell from './AuthShell';

const AdminLogin = ({ loading, onSubmit }) => {
  const [email, setEmail] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileReady, setTurnstileReady] = useState(false);
  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    const handleSuccess = (token) => {
      setTurnstileToken(token);
    };
    const handleError = () => {
      setTurnstileToken('');
    };

    window.onAdminTurnstileSuccess = handleSuccess;
    window.onAdminTurnstileError = handleError;
    window.onAdminTurnstileExpired = handleError;

    const turnstileSiteKey = window.location.href.includes('preprod')
      ? '0x4AAAAAAB4DWYe_933Fn4R9'
      : '0x4AAAAAADbVtQeuR8QRukcT';

    // Wait for Turnstile to be ready and render the widget
    const initTurnstile = () => {
      if (window.turnstile && turnstileRef.current && !widgetIdRef.current) {
        try {
          // Render the widget explicitly
          widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
            sitekey: turnstileSiteKey,
            callback: handleSuccess,
            'error-callback': handleError,
            'expired-callback': handleError,
            theme: 'light',
          });
          setTurnstileReady(true);
          console.log('Turnstile widget rendered successfully');
        } catch (error) {
          console.error('Failed to render Turnstile widget:', error);
        }
      }
    };

    // Check if Turnstile is already loaded
    if (window.turnstile) {
      initTurnstile();
    } else {
      // Wait for Turnstile script to load
      const checkInterval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(checkInterval);
          initTurnstile();
        }
      }, 100);

      // Cleanup interval after 10 seconds
      const timeout = setTimeout(() => {
        clearInterval(checkInterval);
        console.error('Turnstile script failed to load');
      }, 10000);

      return () => {
        clearInterval(checkInterval);
        clearTimeout(timeout);
      };
    }

    return () => {
      delete window.onAdminTurnstileSuccess;
      delete window.onAdminTurnstileError;
      delete window.onAdminTurnstileExpired;
      setTurnstileToken('');
      // Remove the widget if it exists
      try {
        if (window.turnstile && widgetIdRef.current !== null) {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }
      } catch (error) {
        console.warn('Turnstile cleanup failed:', error);
      }
    };
  }, []);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!turnstileToken) {
      alert('Please complete the human verification challenge before proceeding.');
      return;
    }
    onSubmit?.({ email, turnstileToken });
  };

  const handleResetChallenge = () => {
    setTurnstileToken('');
    try {
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.reset(widgetIdRef.current);
        console.log('Turnstile widget reset successfully');
      } else {
        console.warn('Turnstile widget not ready for reset');
      }
    } catch (error) {
      console.error('Turnstile reset failed:', error);
      alert('Failed to reset challenge. Please reload the page.');
    }
  };

  return (
    <AuthShell>
      <form onSubmit={handleSubmit} className="auth-form">
        <div className="mb-3 text-start">
          <label htmlFor="admin-email" className="form-label fw-semibold">
            Admin Email Address
          </label>
          <input
            id="admin-email"
            type="email"
            className="form-control form-control-lg rounded-pill shadow-sm"
            placeholder="you@toivotek.ai"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            disabled={loading}
          />
        </div>
        <div className="mb-4 text-start">
          <label className="form-label fw-semibold">Human Verification</label>
          <div ref={turnstileRef} className="cf-turnstile"></div>
          {!turnstileReady && (
            <div className="text-muted small mt-2">
              Loading verification challenge...
            </div>
          )}
          <div className="d-flex justify-content-between align-items-center mt-2">
            <small className="text-muted">
              Powered by Cloudflare Turnstile for bot-resistant access.
            </small>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary rounded-pill shadow-sm"
              onClick={handleResetChallenge}
              disabled={loading || !turnstileReady}
            >
              Refresh
            </button>
          </div>
        </div>
        <button
          type="submit"
          className="btn btn-primary btn-lg rounded-pill shadow-sm w-100"
          disabled={loading || !email}
        >
          {loading ? 'Requesting OTP…' : 'Send Secure OTP'}
        </button>
      </form>
      <div className="auth-footer mt-4 text-center">
        <small className="text-muted">
          Need help? Contact {' '}
          <a className="link-primary" href="mailto:support@toivotek.ai">
            support@toivotek.ai
          </a>
        </small>
      </div>
    </AuthShell>
  );
};

export default AdminLogin;
